import { SceneTypeDto, VideoElementDto } from "@repo/api/overlays";
import { ElementPositionInput, ElementSizeInput } from "./ElementSliderInput";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { IconAlertCircle, IconVolume } from "@tabler/icons-react";
import ElementAppearanceSection from "./ElementAppearanceSection";
import ElementBorderSection from "./ElementBorderSection";
import ElementShadowSection from "./ElementShadowSection";
import ElementTransitionSection from "./Transition/ElementTransitionSection";
import { CompositionTimings } from "@repo/video-engine-i2v/utils/timing";
import { getResolution } from "@repo/api/utils/composition";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import { AssetMediaAsyncDto } from "@repo/api/asset-media";
import { PercentageInput } from "@/lib/components/PercentageInput";
import { Slider } from "@/lib/components/Slider";
import { useEffect, useState } from "react";

type Props = {
  element: VideoElementDto;
  sceneType: SceneTypeDto;
  compositionTimings: CompositionTimings;
};

export default function VideoElementEditor({
  element,
  sceneType,
  compositionTimings,
}: Props) {
  const assetMedia = useEditorStore((state) => {
    if (!element || !("assetMediaExternalId" in element)) {
      return null;
    }

    return state.assetMedias[element.assetMediaExternalId] ?? null;
  }) as AssetMediaAsyncDto;

  const composition = useEditorStore((state) => state.video!.compositionJson);
  const resolution = getResolution(composition.aspectRatio);

  if (!assetMedia || assetMedia.status === "PENDING") {
    return (
      <div className="flex flex-1 items-center justify-center">
        <LoadingSpinner className="h-8 w-8" />
      </div>
    );
  }

  if (assetMedia.status !== "COMPLETED") {
    return (
      <div className="flex flex-1 flex-col items-center justify-center">
        <IconAlertCircle className="h-6 w-6 text-error" />
        <p className="mt-2 text-sm font-medium text-text-secondary">
          Failed to load video
        </p>
      </div>
    );
  }

  const aspectRatio =
    assetMedia.data.width === null || assetMedia.data.height === null
      ? null
      : assetMedia.data.width / assetMedia.data.height;

  return (
    <div className="px-6">
      <div className="h-24 w-24 overflow-hidden rounded-lg bg-black-400">
        {assetMedia.data.thumbnailSrc ? (
          <img
            src={assetMedia.data.thumbnailSrc}
            className="h-full w-full object-contain object-center"
          />
        ) : (
          <video
            src={assetMedia.data.src}
            playsInline
            controls={false}
            muted
            preload="auto"
            className="h-full w-full object-contain object-center"
          />
        )}
      </div>
      <div className="input-label mt-8">Position & Size</div>
      <div className="flex gap-4">
        <ElementPositionInput
          fieldName="x"
          element={element}
          sceneType={sceneType}
          sliderMax={resolution.width}
        />
        <ElementPositionInput
          fieldName="y"
          element={element}
          sceneType={sceneType}
          sliderMax={resolution.height}
        />
      </div>
      <div className="mt-2 flex gap-4">
        <ElementSizeInput
          fieldName="w"
          element={element}
          aspectRatio={aspectRatio}
          sceneType={sceneType}
          sliderMax={resolution.width}
        />
        <ElementSizeInput
          fieldName="h"
          element={element}
          aspectRatio={aspectRatio}
          sceneType={sceneType}
          sliderMax={resolution.height}
        />
      </div>
      <ElementAppearanceSection element={element} sceneType={sceneType} />
      <VideoVolumeSection element={element} />
      <ElementBorderSection element={element} sceneType={sceneType} />
      <ElementShadowSection element={element} sceneType={sceneType} />
      <ElementTransitionSection
        element={element}
        fieldName="enterAt"
        sceneType={sceneType}
        compositionTimings={compositionTimings}
      />
      <ElementTransitionSection
        element={element}
        fieldName="exitAt"
        sceneType={sceneType}
        compositionTimings={compositionTimings}
      />
    </div>
  );
}

function VideoVolumeSection({ element }: { element: VideoElementDto }) {
  const updateElement = useEditorStore((state) => state.service.updateElement);
  const volume = Math.round((element.volume ?? 1) * 100);
  const [value, setValue] = useState(volume);

  useEffect(() => {
    setValue(volume);
  }, [volume]);

  const onChange = (newValue: number) => {
    setValue(newValue);
    updateElement(element.externalId, (element) => {
      (element as VideoElementDto).volume = newValue / 100;
    });
  };

  return (
    <>
      <div className="input-label mt-8">Volume</div>
      <div className="flex w-74 items-center gap-4">
        <PercentageInput
          className="w-35 shrink-0"
          value={value}
          onChange={onChange}
          leadingComponent={
            <IconVolume className="text-text-secondary" stroke={2} size={17} />
          }
        />
        <Slider
          value={value}
          onValueChange={setValue}
          onValueCommit={onChange}
          min={0}
          max={100}
          step={1}
        />
      </div>
    </>
  );
}
